import 'core-js/es/array';
import 'core-js/proposals/string-replace-all';
import { Provider } from 'react-redux';
import React, { useState, useEffect } from 'react';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import { ApolloProvider as ApolloV3Provider } from 'apollo-3';
import { ApolloProvider } from 'react-apollo';
import { AnalyticsProvider } from '@skooldio/analytics-provider';
import { hotjar } from 'react-hotjar';
import { datadogRum } from '@datadog/browser-rum';
import { isPlatform } from '@ionic/react';
import { StylesProvider, createGenerateClassName } from '@material-ui/core/styles';
import log from 'loglevel';

import { Flex } from 'Components/Base';
import FCMUserListener from 'Containers/FCM/FCMUserListener';
import { MixPanelProviderApp } from 'Domain/MixPanelProviderApp';
import RouteApp from 'Routes/Main';
import {
  getDeviceInfo,
  PushNotifications,
  FirebaseAnalytics,
} from 'Util/Capacitors/SkooldioCapacitor';
import { googleTagManagerInitialize } from 'Util/Analytics/googleTagManager';

import 'intersection-observer';

import store from './Redux/store';
import getConfig, { getAsyncConfig } from './Util/Config';
import './Util/FirebaseService';
import getThemeConfig from './Util/ThemeConfig';
import { CapacitorProvider, CapacitorUtils, constant } from './Util/Capacitors';
import getApolloClient from './GraphQL';
import { ConfigProvider } from './Context/ConfigContext';
import { FirebaseAuthenticationProvider } from './Context/FirebaseAuthenticationContext';
import { OffsetProvider } from './Context/LayoutContext';
import Loading from './Components/Loading';

const {
  googleAnalyticsId: gaId,
  hotjarId,
  storageKey,
  dataDoc = {},
  native = {},
  googleTagManager: gtmConfigs,
} = getConfig();
const theme = getThemeConfig();
const isStorageMode = constant.IS_STORAGE_MODE;
const generateClassName = createGenerateClassName({
  disableGlobal: true,
  productionPrefix: `paas-`,
});

const GlobalStyle = createGlobalStyle`
  * { box-sizing: border-box; }
  html, body, #app {
    height: 100%;
  }
  html {
    /* background-color: #f4f4f4; */
  }
  body {
    margin: 0;
    padding: 0;
    color: ${theme.colors.text};
    font-size: 16px;
    -webkit-overflow-scrolling: touch;
  }
  a {
    color: #444;
  }
  /* overrides semantic ui css font family */
  body,
  h1, h2, h3, h4, h5, h6 {
    font-family: ${theme.fonts.sans};
  }
`;

const { Storage, isNative, getAuthorization } = CapacitorUtils;
const { enablePushNotification } = native;

const initApolloClient = async () => {
  localStorage.removeItem('accessToken');
  const accessToken = await Storage.getItem('accessToken');
  const deviceInfo = await getDeviceInfo();
  if (accessToken) localStorage.setItem('accessToken', accessToken);
  const client = getApolloClient(getAuthorization, accessToken ? false : true, deviceInfo);
  return client;
};
const initLocalStorage = async () => {
  const { keys } = await Storage.key();
  keys.map(async (key) => localStorage.setItem(key, await Storage.getItem(key)));
};
const initGTMConfig = (gtmConfig) => {
  if (gtmConfig?.id) {
    googleTagManagerInitialize(gtmConfig);
  }
};

const isPushPermissionGranted = (permission) =>
  permission?.receive === 'granted' || permission?.granted === true;

const registerPushNotifications = async () => {
  let permission = await PushNotifications.checkPermissions();

  if (permission.receive === 'prompt') {
    permission = await PushNotifications.requestPermissions();
  }

  if (isPushPermissionGranted(permission)) {
    PushNotifications.register();
  }
};

const App = () => {
  const [apolloClient, setApolloClient] = useState(null);
  const [appConfig, setAppConfig] = useState(null);

  useEffect(() => {
    initApolloClient().then((client) => setApolloClient(client));
    getAsyncConfig().then((config) => setAppConfig(config));
    const { enable, applicationId, clientToken, env } = dataDoc;
    if (enable && applicationId && clientToken) {
      datadogRum.init({
        sampleRate: 100,
        trackInteractions: true,
        env: env ?? 'staging',
        // eslint-disable-next-line no-undef
        version: __VERSION__,
        ...dataDoc,
        applicationId,
        clientToken,
      });
    }
    if (hotjarId) hotjar.initialize(hotjarId, 6);

    if (isNative) {
      if (enablePushNotification) {
        registerPushNotifications();

        PushNotifications.addListener('registration', (token) => {
          log.debug('Push registration success, token: ' + token.value);
        });

        PushNotifications.addListener('registrationError', (error) => {
          log.error('Error on registration: ' + JSON.stringify(error));
        });

        PushNotifications.addListener('pushNotificationReceived', (notification) => {
          log.debug('Push received: ' + JSON.stringify(notification));
        });
      }
      if (isPlatform('ios')) {
        FirebaseAnalytics.setCollectionEnabled({
          enabled: true,
        });
      }
      initLocalStorage();
    }

    if (Array.isArray(gtmConfigs) && gtmConfigs.length > 0) {
      gtmConfigs.forEach((gtmConfig) => {
        initGTMConfig(gtmConfig);
      });
      return;
    }

    initGTMConfig(gtmConfigs);
  }, []);

  const isAppReady = apolloClient && appConfig;

  if (!isAppReady) {
    return (
      <ConfigProvider>
        <ThemeProvider theme={theme}>
          <Flex justifyContent={'center'} alignItems={'center'} height={'100vh'}>
            <Loading width={100} height={100} />
          </Flex>
        </ThemeProvider>
      </ConfigProvider>
    );
  }

  const { native } = isAppReady;
  const fcmUserListener =
    isNative && native.enablePushNotification && native.enablePersonalizedNotification ? (
      <FCMUserListener />
    ) : null;

  return (
    <ConfigProvider config={appConfig}>
      <CapacitorProvider>
        <>
          {/* somehow rebass generate an unnecessary div */}
          <StylesProvider generateClassName={generateClassName}>
            <ThemeProvider theme={theme} style={{ height: '100%' }}>
              <>
                <MixPanelProviderApp>
                  <AnalyticsProvider
                    trackingCode={gaId}
                    isStorageMode={isStorageMode}
                    customStorage={Storage}
                    storageKey={storageKey}
                    isNativeApp={isNative}
                  >
                    <ApolloV3Provider client={apolloClient}>
                      <ApolloProvider client={apolloClient}>
                        <Provider store={store}>
                          {fcmUserListener}
                          <FirebaseAuthenticationProvider>
                            <OffsetProvider>
                              <RouteApp />
                            </OffsetProvider>
                          </FirebaseAuthenticationProvider>
                        </Provider>
                      </ApolloProvider>
                    </ApolloV3Provider>
                  </AnalyticsProvider>
                </MixPanelProviderApp>
                <GlobalStyle />
              </>
            </ThemeProvider>
          </StylesProvider>
        </>
      </CapacitorProvider>
    </ConfigProvider>
  );
};

export default App;
